// apps/nos/client/src/components/Workbench/RightRail/VertexRecommendations.tsx
//
// Pinned "Recommended" section that renders at the top of the Add
// Ingredients (IngredientSearch) panel when the user has selected a vertex
// on the Sensory Radar.
//
// Card layout (per row):
//
//     ▌ Name of compound                                            +
//       SWEET · S2  +1.23                            ΔLoss  −0.41
//
// Two numbers replace the older sentence-style "reason" subtitle:
//   • the per-vertex improvement (+Δ) — how much closer this compound moves
//     the live mS toward target at the focused dimension(s)
//   • the signed ΔLoss to the flask's total loss when this compound is
//     added. Sign is leading and same-coloured as the value (green when
//     adding lowers total loss, red when it would raise it).
//
// Ranking comes from the server's nblock call, which mirrors
// build_dashboard.py:wbRefreshSuggestions byte-for-byte
// (suggestForSingleHotspot for a detail axis, suggestByHotspot for a
// receptor group, top-5 by score desc).

import { Icon } from "@/components/Icon/Icon";
import { useVertexRecommendations } from "@/hooks/useVertexRecommendations";
import type {
  VertexRecommendationRow,
  VertexRecommendationsRequest,
} from "@/services/vertexRecommendations_api";
import { VertexRecommendationsApiError } from "@/services/vertexRecommendations_api";
import type { ActiveRadarSelection } from "./SensoryAnalysisRail";

type Props = {
  flavorId: string | null;
  formulaId: number | null;
  versionId: number | null;
  previewId?: string | null;
  selection: ActiveRadarSelection | null;
  hasFormulation: boolean;
  onAdd: (compoundId: string) => void;
  /** DEMO (impl-demo-mock) — when provided, a row click routes here with the
   *  full row (so the demo can mock the viz/loss move from origSubstanceId)
   *  instead of the plain onAdd. Falls back to onAdd when absent. */
  onApplyRecommendation?: (row: VertexRecommendationRow) => void;
  inFlask: ReadonlySet<string>;
};

const RESULT_COUNT = 5;

export function VertexRecommendations({
  flavorId,
  formulaId,
  versionId,
  previewId = null,
  selection,
  hasFormulation,
  onAdd,
  onApplyRecommendation,
  inFlask,
}: Props) {
  const request: VertexRecommendationsRequest | null =
    selection == null
      ? null
      : selection.kind === "detail"
        ? { scope: "detail", dimIdx: selection.dimIdx, k: RESULT_COUNT }
        : { scope: "receptor", dimIdxs: selection.dimIdxs, k: RESULT_COUNT };

  const query = useVertexRecommendations({
    flavorId,
    formulaId,
    versionId,
    request: hasFormulation ? request : null,
    previewId,
  });

  if (selection == null) return null;

  return (
    <div className="vrec">
      <div className="vrec-head">
        <div className="vrec-head-left">
          <Icon name="wand" size={12} />
          <span className="label">Recommended for</span>
          <span className="vrec-target">{selection.label}</span>
        </div>
        <span className="mono vrec-count">
          {query.data?.rows?.length ?? 0} suggested
        </span>
      </div>

      <Body
        query={query}
        hasFormulation={hasFormulation}
        onAdd={onAdd}
        onApplyRecommendation={onApplyRecommendation}
        inFlask={inFlask}
        selection={selection}
      />
    </div>
  );
}

function Body({
  query,
  hasFormulation,
  onAdd,
  onApplyRecommendation,
  inFlask,
  selection,
}: {
  query: ReturnType<typeof useVertexRecommendations>;
  hasFormulation: boolean;
  onAdd: (compoundId: string) => void;
  onApplyRecommendation?: (row: VertexRecommendationRow) => void;
  inFlask: ReadonlySet<string>;
  selection: ActiveRadarSelection;
}) {
  if (!hasFormulation) {
    return (
      <Hint>
        Add ingredients to the flask to see recommendations for this vertex.
      </Hint>
    );
  }
  if (query.isLoading) {
    return <Hint>Finding ingredients…</Hint>;
  }
  if (query.error) {
    if (query.error instanceof VertexRecommendationsApiError) {
      if (query.error.code === "no_target") {
        return <Hint>Set a target flavor to unlock recommendations.</Hint>;
      }
      if (query.error.code === "no_formulation") {
        return (
          <Hint>Add ingredients to the flask to see recommendations.</Hint>
        );
      }
    }
    return <Hint>Couldn’t load recommendations. Please try again.</Hint>;
  }
  const rows = query.data?.rows ?? [];
  if (rows.length === 0) {
    return <Hint>No improving ingredient found.</Hint>;
  }
  return (
    <div className="vrec-list">
      {rows.map((r) => {
        const isIn = inFlask.has(r.compoundId);
        // ΔLoss sign convention: negative = improving total loss (good →
        // green), positive = regressing total loss (bad → red). The chip
        // and the vertex Δ use independent tints because a row can move
        // the focused vertex toward target while still raising overall
        // loss (or vice-versa) — keep both tints honest.
        const lossSign = signOf(-r.deltaLoss);
        return (
          <button
            key={r.compoundId}
            type="button"
            className={`vrec-row${isIn ? " is-in" : ""}`}
            // Clicking an in-flask recommendation "doses it up" +10% (the bump
            // lives in WorkbenchPage.onAdd); a new one is added. Non-demo
            // recommendation lists never contain an in-flask compound (the
            // candidate pool excludes flask members), so the in-flask branch
            // only fires for the demo's scripted "dose-up" rows.
            onClick={() =>
              onApplyRecommendation
                ? onApplyRecommendation(r)
                : onAdd(r.compoundId)
            }
            title={
              isIn
                ? `In flask — click to dose up +10% · total ΔLoss ${signedNumber(r.deltaLoss, 3)}`
                : `ΔLoss at ${selection.label}: ${signedNumber(-r.improvement, 3)} · total ΔLoss ${signedNumber(r.deltaLoss, 3)}`
            }
          >
            <div className="vrec-row-top">
              <span
                className="vrec-fam"
                style={{
                  background: r.family?.color ?? "var(--fam-default, #cfcec3)",
                }}
              />
              <span className="vrec-name">{r.name}</span>
              <Icon name={isIn ? "check" : "plus"} size={12} />
            </div>

            <div className="vrec-row-meta">
              <span className="vrec-vertex" title={selection.label}>
                <span className="vrec-vertex-label">{selection.label}</span>
                {/* Per-vertex ΔLoss = −improvement (improvement is a focus-gain,
                    so its negative is the vertex's loss change): negative = good
                    = green, matching the total ΔLoss column. Color keys off the
                    gain sign (positive gain = good = "pos" = green). */}
                <span
                  className="mono vrec-vertex-delta"
                  data-sign={signOf(r.improvement)}
                >
                  {signedNumber(-r.improvement, 2)}
                </span>
              </span>

              <span className="vrec-delta-loss">
                <span className="mono vrec-delta-loss-label">ΔLoss</span>
                <span
                  className="mono vrec-delta-loss-value"
                  data-sign={lossSign}
                >
                  {signedNumber(r.deltaLoss, 2)}
                </span>
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// "pos" = good (improving), "neg" = bad (regressing), "zero" = neutral.
// Used by CSS to tint the value + label with the green/red/muted ramp.
function signOf(x: number): "pos" | "neg" | "zero" {
  if (x > 0.001) return "pos";
  if (x < -0.001) return "neg";
  return "zero";
}

// Always renders the sign in FRONT of the magnitude, using the typographic
// minus (U+2212) for negatives so the column reads "−0.41" instead of the
// shorter hyphen-minus "-0.41" — the minus then visually matches the "+"
// width and the row stays column-aligned.
function signedNumber(x: number, digits: number): string {
  if (x >= 0) return `+${x.toFixed(digits)}`;
  return `−${Math.abs(x).toFixed(digits)}`;
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div className="vrec-hint">{children}</div>;
}
